/* eslint-disable max-lines */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Alert,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  SwipeableDrawer,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Hearing as HearingIcon,
  Warning as WarningIcon,
  SmartToy as BotIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Note as NoteIcon,
  NoteAdd as NoteAddIcon,
  Assessment as ReportIcon,
  Computer as ComputerIcon,
  Tablet as TabletIcon,
  PhoneAndroid as PhoneIcon,
  Menu as MenuIcon,
  Psychology as AgentIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import api from '../../services/api';
import { FaanaVoiceService } from '../../services/voice/FaanaVoiceService';
import TokenizedText from './TokenizedText';
import FaanaLogo from './FaanaLogo';
import ReportGeneration from './ReportGeneration';
import VariableProcessingDisplay from './VariableProcessingDisplay';
import ReportCompletionDisplay from './ReportCompletionDisplay';
import PromptDisplay from './PromptDisplay';
import useMobileDetection from '../../hooks/useMobileDetection';
import ChatErrorMessage from './ChatErrorMessage';
import StreamingMessage from './StreamingMessage';
import { Stop as StopIcon } from '@mui/icons-material';

// Faana Logo Avatar component
const FaanaAvatar = ({ size = 40 }) => {
  const [logoError, setLogoError] = useState(false);

  return (
    <Avatar
      sx={{
        bgcolor: 'white',
        width: size,
        height: size,
        border: '2px solid #f0f0f0',
        padding: '4px',
        overflow: 'hidden',
      }}
    >
      {logoError ? (
        <BotIcon sx={{ color: 'primary.main' }} />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="img"
            src="/faana_logo.png"
            alt="Faana"
            onError={() => setLogoError(true)}
            sx={{
              width: '70%',
              height: '70%',
              objectFit: 'contain',
            }}
          />
        </Box>
      )}
    </Avatar>
  );
};

// Cache for failed photo requests (1 hour timeout)
const photoFailureCache = new Map();
const PHOTO_FAILURE_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds

// User Avatar component that uses cached photos
const UserAvatar = ({ user, size = 40 }) => {
  const { user: currentUser } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.email && !imageError && currentUser && !loading) {
      // Check if we've recently failed to load this user's photo
      const cacheKey = user.email;
      const cachedFailure = photoFailureCache.get(cacheKey);

      if (cachedFailure && (Date.now() - cachedFailure) < PHOTO_FAILURE_TIMEOUT) {
        // Skip loading if we failed within the last hour
        setImageError(true);
        return;
      }

      const loadImage = async () => {
        setLoading(true);
        try {
          const token = await currentUser.getIdToken();
          const photoUrl = api.getUserPhotoUrl(user.email);
          const response = await fetch(photoUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            mode: 'cors',
            credentials: 'omit'
          });

          if (response.ok) {
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setImageSrc(objectUrl);
            setImageError(false);
            // Clear any cached failure for this user
            photoFailureCache.delete(cacheKey);
          } else if (response.status === 404) {
            // Cache 404 failures to prevent retrying for 1 hour
            photoFailureCache.set(cacheKey, Date.now());
            setImageError(true);
          } else {
            setImageError(true);
          }
        } catch (_error) {
          // Cache network/other errors as well
          photoFailureCache.set(cacheKey, Date.now());
          setImageError(true);
          setImageSrc(null);
        } finally {
          setLoading(false);
        }
      };

      loadImage();
    }

    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, currentUser]);

  const getInitials = (user) => {
    if (user?.displayName) {
      const names = user.displayName.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[1][0]).toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || '?';
  };

  if (loading) {
    return (
      <Avatar sx={{ width: size, height: size, bgcolor: 'grey.300' }}>
        <CircularProgress size={size * 0.6} sx={{ color: 'grey.500' }} />
      </Avatar>
    );
  }

  if (imageSrc && !imageError) {
    return (
      <Avatar src={imageSrc} sx={{ width: size, height: size }}>
        {getInitials(user)}
      </Avatar>
    );
  }

  return (
    <Avatar sx={{
      width: size,
      height: size,
      bgcolor: 'secondary.main',
      color: 'white'
    }}>
      {getInitials(user)}
    </Avatar>
  );
};

// Component to display MCP tool details with expand/collapse
const MCPToolDetails = ({ tools }) => {
  const [expanded, setExpanded] = useState(false);

  if (!tools || tools.length === 0) return null;

  return (
    <Box>
      <Button
        size="small"
        variant="text"
        onClick={() => setExpanded(!expanded)}
        startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          textTransform: 'none',
          color: 'info.dark',
          fontSize: '0.75rem',
          p: 0,
          minWidth: 'auto',
          '&:hover': { bgcolor: 'transparent' }
        }}
      >
        Show {expanded ? 'less' : 'technical'} details
      </Button>
      <Collapse in={expanded}>
        <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', display: 'block' }}>
            {JSON.stringify(tools.map(t => ({
              tool: t.tool,
              input: t.input,
              resourcesAccessed: t.resourcesAccessed
            })), null, 2)}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * Replace raw token IDs (e.g. PER_1a702319a69a) with human-readable labels
 * for display in contexts where calling the detokenize API isn't practical
 * (chat list previews, TTS, etc.)
 */
const TOKEN_LABELS = {
  PER: 'Person', ORG: 'Organization', LOC: 'Location', ADR: 'Address',
  DTE: 'Date', DOB: 'Date', EML: 'Email', PHN: 'Phone', CCN: 'Card',
  SSN: 'SSN', DMG: 'Info', CRD: 'Credential', DIG: 'ID', GID: 'ID',
  MED: 'Medical', TKN: 'Token',
};
const stripTokens = (text) =>
  text ? text.replace(/\b([A-Z]{2,4})_[a-f0-9]{12}\b/g, (_, prefix) =>
    `[${TOKEN_LABELS[prefix] || 'Redacted'}]`) : '';

// eslint-disable-next-line max-lines-per-function
const ChatBot = ({ entityId, entityName, fullPage = false }) => {
  const { user } = useAuth();
  const { settings: userPreferences } = useUserPreferences();
  const { isMobile } = useMobileDetection();
  const [open, setOpen] = useState(fullPage);
  const [chatListCollapsed, setChatListCollapsed] = useState(() => {
    // Load saved preference from localStorage
    const savedState = localStorage.getItem('chatListCollapsed');
    return savedState !== null ? JSON.parse(savedState) : true; // Default to collapsed
  });
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatMenuAnchor, setChatMenuAnchor] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [renamingChat, setRenamingChat] = useState(null);
  const [newChatTitle, setNewChatTitle] = useState('');
  const [error, setError] = useState(null);
  const [availablePrompts, setAvailablePrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [showPromptSelector, setShowPromptSelector] = useState(false);
  const [noteTakingMode, setNoteTakingMode] = useState(false);
  const [_currentNoteId, setCurrentNoteId] = useState(null);

  // Streaming states
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingToolActivity, setStreamingToolActivity] = useState([]);
  const streamAbortRef = useRef(null);
  const streamContentRef = useRef('');
  const streamingUpdateTimer = useRef(null);
  const reportPollTimerRef = useRef(null);

  // Voice states
  const [activeMicMode, setActiveMicMode] = useState(false);
  const [voiceResponseMode, setVoiceResponseMode] = useState(false);
  const [voiceService, setVoiceService] = useState(null);
  const [_isRecording, setIsRecording] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('idle');
  const [showChromeWarning, setShowChromeWarning] = useState(false);
  const [voiceTranscriptBuffer, setVoiceTranscriptBuffer] = useState('');
  const [showReportGeneration, setShowReportGeneration] = useState(false);
  const [reportId, setReportId] = useState(null);
  const [reportStatus, setReportStatus] = useState(null);
  const [reportPreview, setReportPreview] = useState({ open: false, content: '', reportId: null, savedReportId: null });
  const [previewDevice, setPreviewDevice] = useState('desktop'); // desktop, tablet, mobile

  // Device preview dimensions
  const getDeviceDimensions = (device) => {
    switch (device) {
      case 'mobile':
        return { width: '375px', height: '667px' };
      case 'tablet':
        return { width: '768px', height: '1024px' };
      case 'desktop':
      default:
        return { width: '100%', height: 'auto' };
    }
  };
  const messagesEndRef = useRef(null);
  const sendMessageRef = useRef(null);

  // Cleanup voice service on unmount — releases mic and stops recognition
  useEffect(() => {
    return () => {
      if (voiceService) {
        voiceService.stop();
        voiceService.stopSpeaking();
      }
    };
  }, [voiceService]);

  // Clean up report polling timer on unmount
  useEffect(() => {
    return () => {
      if (reportPollTimerRef.current) {
        clearTimeout(reportPollTimerRef.current);
        reportPollTimerRef.current = null;
      }
    };
  }, []);

  // Auto-collapse chat list on mobile
  useEffect(() => {
    setChatListCollapsed(isMobile);
  }, [isMobile]);

  // Function to toggle message collapse state
  const toggleMessageCollapse = (index) => {
    setMessages(prev => prev.map((msg, i) =>
      i === index ? { ...msg, isCollapsed: !msg.isCollapsed } : msg
    ));
  };

  // Function to toggle MCP logs collapse state
  const toggleMcpLogsCollapse = (index) => {
    setMessages(prev => prev.map((msg, i) =>
      i === index ? { ...msg, mcpLogsCollapsed: !msg.mcpLogsCollapsed } : msg
    ));
  };

  // Debug logging
  console.log('ChatBot render - open:', open, 'entityId:', entityId, 'activeChat:', activeChat);

  // Load chats when entity changes
  useEffect(() => {
    console.log('Load chats effect - entityId:', entityId, 'open:', open);
    if (entityId && open) {
      loadChats();
      loadAvailablePrompts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, open]);

  // Load chat history when active chat changes
  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    console.log('useEffect triggered - activeChat:', activeChat, 'entityId:', entityId);
    if (!activeChat || !entityId) {
      console.log('Conditions not met - activeChat:', !!activeChat, 'entityId:', !!entityId);
      return;
    }

    console.log('Conditions met, loading chat history for:', activeChat);

    // eslint-disable-next-line max-lines-per-function
    const loadHistory = async () => {
      try {
        setLoading(true);
        setMessages([]); // Clear messages immediately for better UX
        console.log('Making API call for chat history:', { entityId, activeChat });
        const response = await api.getChatHistory(entityId, activeChat);
        console.log('Chat history response:', response);
        setMessages(response.messages || []);

        // Check for in-progress reports and resume polling
        const messages = response.messages || [];
        const lastReportMessage = messages.filter(m => m.isReportGeneration).pop();

        if (lastReportMessage && lastReportMessage.reportStatus &&
            lastReportMessage.reportStatus !== 'completed' &&
            lastReportMessage.reportStatus !== 'error' &&
            lastReportMessage.reportStatus !== 'all_variables_processed') {
          // Found a potentially in-progress report
          console.log('Found potentially in-progress report:', {
            reportId: lastReportMessage.reportId,
            status: lastReportMessage.reportStatus,
            progress: `${lastReportMessage.processedVariables}/${lastReportMessage.totalVariables}`,
            lastPollTime: lastReportMessage.lastPollTime
          });

          const reportIdToResume = lastReportMessage.reportId;
          const resumeEntityId = lastReportMessage.entityId || entityId;

          if (reportIdToResume) {
            // eslint-disable-next-line max-depth
            try {
              // Verify the report is actually still in progress
              console.log('Checking current report status with API...');
              const currentStatus = await api.getReportQuickStatus(reportIdToResume, resumeEntityId);

              // eslint-disable-next-line max-depth
              if (currentStatus.success &&
                  currentStatus.status !== 'completed' &&
                  currentStatus.status !== 'error' &&
                  currentStatus.status !== 'all_variables_processed') {
                // Report is still in progress, resume polling
                console.log('Report confirmed still in progress, resuming:', {
                  currentStatus: currentStatus.status,
                  currentProgress: `${currentStatus.processedVariables}/${currentStatus.totalVariables}`
                });

                // Set the report state
                setReportId(reportIdToResume);
                setReportStatus(currentStatus.status);

                // Update the message with current status if it changed
                // eslint-disable-next-line max-depth
                if (currentStatus.processedVariables !== lastReportMessage.processedVariables) {
                  // Get full status to update the UI
                  const fullStatus = await api.getReportStatus(reportIdToResume, resumeEntityId);
                  // eslint-disable-next-line max-depth
                  if (fullStatus.success) {
                    setMessages(prev => prev.map(msg =>
                      msg.isReportGeneration && msg.reportId === reportIdToResume
                        ? {
                            ...msg,
                            reportStatus: fullStatus.status,
                            processedVariables: fullStatus.processedVariables,
                            totalVariables: fullStatus.totalVariables,
                            errorCount: fullStatus.errorCount,
                            insufficientDataCount: fullStatus.insufficientDataCount,
                            variables: fullStatus.variables || msg.variables
                          }
                        : msg
                    ));
                  }
                }

                // Resume polling
                console.log('Starting report polling for resumed report:', reportIdToResume);
                startReportPolling(reportIdToResume, lastReportMessage.id || `resumed_${Date.now()}`, resumeEntityId);
              } else {
                // Report has completed while we were away
                console.log('Report completed while offline, updating status');

                // Get final status
                const finalStatus = await api.getReportStatus(reportIdToResume, resumeEntityId);
                // eslint-disable-next-line max-depth
                if (finalStatus.success) {
                  // Update the existing message to completed state
                  setMessages(prev => prev.map(msg =>
                    msg.isReportGeneration && msg.reportId === reportIdToResume
                      ? {
                          ...msg,
                          reportStatus: finalStatus.status,
                          processedVariables: finalStatus.totalVariables,
                          totalVariables: finalStatus.totalVariables,
                          errorCount: finalStatus.errorCount || 0,
                          insufficientDataCount: finalStatus.insufficientDataCount || 0,
                          variables: finalStatus.variables || []
                        }
                      : msg
                  ));

                  // Add completion message if not already present
                  const hasCompletionMsg = messages.some(m => m.isReportCompletion && m.reportId === reportIdToResume);
                  // eslint-disable-next-line max-depth
                  if (!hasCompletionMsg && finalStatus.status === 'completed') {
                    const completedVars = finalStatus.variables?.filter(v => v.status === 'completed') || [];
                    const errorVars = finalStatus.variables?.filter(v => v.status === 'error') || [];
                    const insufficientVars = finalStatus.variables?.filter(v => v.status === 'insufficient_data') || [];

                    const completionMessage = {
                      role: 'assistant',
                      content: '',
                      timestamp: new Date().toISOString(),
                      isReportCompletion: true,
                      reportComplete: true,
                      reportId: reportIdToResume,
                      totalVariables: finalStatus.totalVariables,
                      completedVariables: completedVars,
                      insufficientDataVariables: insufficientVars,
                      errorVariables: errorVars
                    };

                    setMessages(prev => [...prev, completionMessage]);
                    await saveReportMessage(completionMessage);
                  }
                }
              }
            } catch (error) {
              console.error('Error checking report status during resume:', error);
              // Don't resume polling if we can't verify status
            }
          }
        }

        // Restore note-taking state from chat metadata
        // Each chat maintains its own note-taking state
        try {
          // Get chat metadata to restore note-taking state
          const chatMetadata = await api.getChatMetadata(entityId, activeChat);
          if (chatMetadata) {
            setNoteTakingMode(chatMetadata.isNoteTakingActive || false);
            setCurrentNoteId(chatMetadata.noteId || null);
          } else {
            // Fallback: reset state if no metadata found
            setNoteTakingMode(false);
            setCurrentNoteId(null);
          }
        } catch (metaError) {
          console.warn('Failed to load chat metadata, resetting note state:', metaError);
          setNoteTakingMode(false);
          setCurrentNoteId(null);
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
        setError('Failed to load messages');
        setMessages([]); // Clear on error too
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat, entityId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Save report-related messages to Firestore
  const saveReportMessage = async (messageData) => {
    if (!entityId || !activeChat) {
      console.warn('Cannot save report message - missing entityId or activeChat');
      return;
    }

    try {
      await api.saveReportMessage(entityId, activeChat, messageData);
      console.log('Report message saved to Firestore');
    } catch (error) {
      console.error('Failed to save report message:', error);
      // Don't throw - we don't want to interrupt the UI flow
    }
  };

  const loadChats = async () => {
    try {
      setLoading(true);
      console.log('Loading chats for entity:', entityId);
      const response = await api.listChats(entityId);
      console.log('Loaded chats:', response.chats);
      setChats(response.chats || []);

      // If no active chat and chats exist, select the first one
      if (!activeChat && response.chats?.length > 0) {
        console.log('No active chat, selecting first:', response.chats[0].chatId);
        setActiveChat(response.chats[0].chatId);
      } else {
        console.log('Active chat already set or no chats available:', activeChat);
      }
    } catch (error) {
      console.error('Failed to load chats:', error);
      setError('Failed to load chat history');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePrompts = async () => {
    try {
      setLoadingPrompts(true);
      const response = await api.listPrompts({ category: 'chat', isActive: true });
      console.log('Loaded prompts:', response.prompts);
      setAvailablePrompts(response.prompts || []);

      // Set default prompt if none selected
      if (!selectedPrompt && response.prompts?.length > 0) {
        const defaultPrompt = response.prompts.find(p => p.isDefault) || response.prompts[0];
        setSelectedPrompt(defaultPrompt);
      }
    } catch (error) {
      console.error('Failed to load prompts:', error);
      // Don't show error to user as this is optional functionality
    } finally {
      setLoadingPrompts(false);
    }
  };

  const createNewChat = async () => {
    try {
      const response = await api.createChat(entityId, `Chat ${new Date().toLocaleDateString()}`);
      await loadChats();
      setActiveChat(response.chatId);
      setMessages([]);
    } catch (error) {
      console.error('Failed to create chat:', error);
      setError('Failed to create new chat');
    }
  };

  // eslint-disable-next-line max-lines-per-function
  const startReportPolling = (reportId, placeholderId, entityId) => {
    console.log('=== startReportPolling CALLED ===', { reportId, placeholderId, entityId });
    let lastProcessedVariables = 0;
    let pollCount = 0;
    let lastVariableResults = [];
    let lastVersion = 0;
    let lastStatus = '';

    const stopPolling = () => {
      if (reportPollTimerRef.current) {
        clearTimeout(reportPollTimerRef.current);
        reportPollTimerRef.current = null;
      }
    };

    const scheduleNextPoll = () => {
      reportPollTimerRef.current = setTimeout(doPoll, 2000);
    };

    // eslint-disable-next-line max-lines-per-function
    const doPoll = async () => {
      try {
        pollCount++;
        console.log(`=== Polling report status (attempt ${pollCount}):`, reportId);

        // First, try quickStatus for lightweight version check
        let quickResponse;
        let response;

        try {
          quickResponse = await api.getReportQuickStatus(reportId, entityId);

          if (!quickResponse.success) {
            throw new Error(quickResponse.error || 'Quick status failed');
          }

          // Only fetch full details if version has changed or this is the first poll
          if (quickResponse.version > lastVersion || pollCount === 1) {
            lastVersion = quickResponse.version;
            console.log(`=== Version changed to ${lastVersion}, fetching full details ===`);
            response = await api.getReportStatus(reportId, entityId);
          } else {
            // Use the lightweight response for status checking - FIXED: include all required fields
            response = {
              success: true,
              reportId, // FIXED: Added missing field
              status: quickResponse.status,
              totalVariables: quickResponse.totalVariables,
              processedVariables: quickResponse.processedVariables,
              errorCount: quickResponse.errorCount,
              insufficientDataCount: quickResponse.insufficientDataCount,
              variables: lastVariableResults || [], // Use cached variable results
              updatedAt: quickResponse.lastUpdated // FIXED: Added missing field
            };
            console.log(`=== No version change (${lastVersion}), using cached data ===`);
          }
        } catch (quickError) {
          // FIXED: Fallback to full status if quick status fails
          console.warn('Quick status failed, falling back to full status:', quickError.message);
          response = await api.getReportStatus(reportId, entityId);
        }

        if (response.success) {
          setReportStatus(response.status);

          const totalVariables = response.totalVariables || 0;
          const processedVariables = response.processedVariables || 0;
          const errorCount = response.errorCount || 0;
          const insufficientDataCount = response.insufficientDataCount || 0;
          const variables = response.variables || [];

          // Check if there are new variables processed
          if (processedVariables > lastProcessedVariables || pollCount === 1) {
            lastProcessedVariables = processedVariables;

            // No need to build progress content anymore - handled by VariableProcessingDisplay component

            // Update the placeholder message or create it if it doesn't exist
            setMessages(prev => {
              // Check if we're resuming and need to find the existing report message
              let targetMessageId = placeholderId;
              if (placeholderId.startsWith('resumed_')) {
                // Find the last report generation message to update
                const existingReportMsg = prev.filter(m => m.isReportGeneration && m.reportId === reportId).pop();
                if (existingReportMsg && existingReportMsg.id) {
                  targetMessageId = existingReportMsg.id;
                }
              }

              const messageExists = prev.some(msg => msg.id === targetMessageId);

              if (messageExists) {
                // Update existing message
                return prev.map(msg =>
                  msg.id === targetMessageId
                    ? {
                        ...msg,
                        role: 'assistant',
                        content: '', // No content needed since we use VariableProcessingDisplay
                        timestamp: new Date().toISOString(),
                        isReportGeneration: true,
                        loading: false, // VariableProcessingDisplay handles its own loading states
                        reportId: reportId,  // Include report ID for resuming
                        reportStatus: response.status,
                        totalVariables,
                        processedVariables,
                        errorCount,
                        insufficientDataCount,
                        variables,
                        pollCount,
                        entityId: entityId,  // Include for resume
                        lastPollTime: Date.now()
                      }
                    : msg
                );
              } else {
                // Create new message if it doesn't exist
                return [...prev, {
                  id: placeholderId,
                  role: 'assistant',
                  content: '', // No content needed since we use VariableProcessingDisplay
                  timestamp: new Date().toISOString(),
                  isReportGeneration: true,
                  loading: false, // VariableProcessingDisplay handles its own loading states
                  reportId: reportId,  // Include report ID for resuming
                  reportStatus: response.status,
                  totalVariables,
                  processedVariables,
                  errorCount,
                  insufficientDataCount,
                  variables,
                  pollCount,
                  entityId: entityId,  // Include for resume
                  lastPollTime: Date.now()
                }];
              }
            });

            // Store latest variable results for caching
            // eslint-disable-next-line max-depth
            if (response.variables) {
              lastVariableResults = response.variables;
            }

            // Save report progress message to Firestore with intelligent saving
            const shouldSave = pollCount === 1 || // First poll
                             processedVariables !== lastProcessedVariables || // Progress made
                             processedVariables === totalVariables || // Completed
                             (processedVariables > 0 && processedVariables % 5 === 0) || // Every 5 variables
                             response.status !== lastStatus || // Status changed
                             errorCount > 0 || insufficientDataCount > 0; // Issues detected

            // eslint-disable-next-line max-depth
            if (shouldSave) {
              await saveReportMessage({
                role: 'assistant',
                content: '', // No content needed since we use VariableProcessingDisplay
                timestamp: new Date().toISOString(),
                isReportGeneration: true,
                loading: false,
                reportId: reportId,  // Include the report ID for resuming
                reportStatus: response.status,
                totalVariables,
                processedVariables,
                errorCount,
                insufficientDataCount,
                variables: lastVariableResults || [],
                entityId: entityId, // Include for resume API calls
                lastPollTime: Date.now(),
                pollVersion: pollCount
              });

              // Track last saved status
              lastStatus = response.status;
            }
          }

          // Stop polling if report is complete or errored (use quickResponse for immediate status check)
          const currentStatus = quickResponse.status || response.status;
          if (currentStatus === 'completed' || currentStatus === 'error' || currentStatus === 'all_variables_processed') {
            stopPolling();

            // Remove the placeholder message to prevent it from showing initialization state
            setMessages(prev => prev.filter(msg => msg.id !== placeholderId));

            setReportId(null);
            setReportStatus(null);
            setSendingMessage(false);

            // Show final message
            // eslint-disable-next-line max-depth
            if (response.status === 'completed' || response.status === 'all_variables_processed') {
              const completedVariables = lastVariableResults?.filter(v => v.status === 'completed') || [];
              const insufficientDataVariables = lastVariableResults?.filter(v => v.status === 'insufficient_data') || [];
              const errorVariables = lastVariableResults?.filter(v => v.status === 'error') || [];

              const completionMessage = {
                role: 'assistant',
                content: '', // Content handled by ReportCompletionDisplay
                timestamp: new Date().toISOString(),
                isReportCompletion: true,
                reportComplete: true,
                reportId: reportId,
                totalVariables,
                completedVariables,
                insufficientDataVariables,
                errorVariables
              };

              setMessages(prev => [...prev, completionMessage]);

              // Save completion message to Firestore
              await saveReportMessage(completionMessage);
            } else if (response.status === 'error') {
              const errorMessage = {
                role: 'assistant',
                content: `❌ **Report Generation Failed**\n\nAn error occurred during report generation.\n\n**What you can do:**\n1. Try starting a new report with a different template\n2. Check that your template has valid variables\n3. Ensure you have the necessary permissions\n\nIf the issue persists, please contact support with the following:\n- Report ID: ${reportId}\n- Timestamp: ${new Date().toISOString()}`,
                timestamp: new Date().toISOString(),
                isReportGeneration: false  // Changed to false since this is an error message, not the progress display
              };

              setMessages(prev => [...prev, errorMessage]);

              // Save error message to Firestore
              await saveReportMessage(errorMessage);
            }
            return; // Terminal — don't schedule next poll
          }
        }
        // Still in progress — schedule next poll
        scheduleNextPoll();
      } catch (error) {
        console.error('Error polling report status:', error);
        stopPolling();
        setReportId(null);
        setReportStatus(null);
        setSendingMessage(false);

        // Remove the placeholder message to prevent confusion
        setMessages(prev => prev.filter(msg => msg.id !== placeholderId));

        // Add error message
        const catchErrorMessage = {
          role: 'assistant',
          content: '❌ Error generating report. Please try again.',
          timestamp: new Date().toISOString(),
          loading: false,
          isReportGeneration: false
        };

        setMessages(prev => [...prev, catchErrorMessage]);

        // Save error message to Firestore
        await saveReportMessage(catchErrorMessage);
      }
    };

    // Start the first poll immediately, then chain via setTimeout
    doPoll();
  };

  // eslint-disable-next-line max-lines-per-function
  const handleStartReport = async (reportConfig) => {
    console.log('Starting report generation:', reportConfig);

    // Close the report generation modal
    setShowReportGeneration(false);

    // Construct a formatted message showing the report configuration with full prompt details
    const configMessage = {
      summary: `🚀 Starting ${reportConfig.reportType.name}`,
      fullContent: `**Report Configuration:**
      
**Report Type:** ${reportConfig.reportType.name}
${reportConfig.reportType.description}

**AI Instructions:** ${reportConfig.prompt.name}
${reportConfig.prompt.description || ''}

**Template:** ${reportConfig.template.name}
${reportConfig.template.description || 'Standard report template'}

**Full Prompt Content:**
${reportConfig.prompt.template || 'Prompt template not available'}

---

I'll now begin generating your ${reportConfig.reportType.name.toLowerCase()}. This process typically takes ${reportConfig.reportType.estimatedTime}.

**What to expect:**
1. 🔍 I'll analyze each variable in your template one by one
2. 📊 You'll see real-time progress as I research each variable
3. 💡 I may need your input if I have questions
4. ✅ Once all variables are processed, I'll compile your final report

Let's get started...`
    };

    // Send this as a special system message to start the report
    try {
      setSendingMessage(true);

      // Add the configuration message to the chat
      const reportStartMessage = {
        role: 'user',
        content: configMessage.fullContent,
        contentSummary: configMessage.summary,
        timestamp: new Date().toISOString(),
        isReportStart: true,
        reportConfig: reportConfig,
        isCollapsible: true,
        isCollapsed: true  // Start collapsed to show summary first
      };

      setMessages(prev => [...prev, reportStartMessage]);

      // Save the report start message to Firestore
      await saveReportMessage(reportStartMessage);

      // Create a placeholder for the AI response
      const placeholderId = `placeholder_${Date.now()}`;
      setMessages(prev => [...prev, {
        id: placeholderId,
        role: 'assistant',
        content: '🚀 **Initializing Report Generation**\n\nExtracting variables from your template...',
        loading: false,
        isReportGeneration: true,
        timestamp: new Date().toISOString(),
        reportStatus: 'initializing',
        totalVariables: 0,
        processedVariables: 0,
        errorCount: 0,
        insufficientDataCount: 0,
        variables: []
      }]);

      // Call the report generation API
      const response = await api.generateAIReport({
        entityId: reportConfig.entityId,
        reportType: reportConfig.reportType.id,
        promptId: reportConfig.prompt.id,
        templateId: reportConfig.template.id,
        parameters: {}
      });

      if (response.success) {
        // Store the report ID for polling
        setReportId(response.reportId);
        setReportStatus(response.status || 'initializing');

        // Log for debugging
        console.log('Report generation started:', {
          reportId: response.reportId,
          status: response.status,
          totalVariables: response.totalVariables
        });

        // CRITICAL: Update the placeholder message with the report ID
        setMessages(prev => prev.map(msg =>
          msg.id === placeholderId
            ? {
                ...msg,
                reportId: response.reportId,
                reportStatus: response.status || 'initializing',
                totalVariables: response.totalVariables || 0,
                entityId: reportConfig.entityId
              }
            : msg
        ));

        // Save the updated message with report ID to Firestore
        await saveReportMessage({
          role: 'assistant',
          content: '🚀 **Initializing Report Generation**\n\nExtracting variables from your template...',
          timestamp: new Date().toISOString(),
          isReportGeneration: true,
          loading: false,
          reportId: response.reportId,
          reportStatus: response.status || 'initializing',
          totalVariables: response.totalVariables || 0,
          processedVariables: 0,
          errorCount: 0,
          insufficientDataCount: 0,
          variables: [],
          entityId: reportConfig.entityId
        });

        // Start polling for updates with reportId and entityId
        startReportPolling(response.reportId, placeholderId, reportConfig.entityId);
      } else {
        throw new Error(response.error || 'Failed to start report generation');
      }

    } catch (error) {
      console.error('Failed to start report generation:', error);
      setError('Failed to start report generation: ' + error.message);
      setSendingMessage(false);
    }
  };

  // Flush streaming content to state (batched updates)
  const flushStreamContent = useCallback(() => {
    setStreamingContent(streamContentRef.current);
  }, []);

  // Stop streaming handler
  const handleStopStreaming = useCallback(() => {
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
    const finalContent = streamContentRef.current;
    const msgId = streamingMessageId;
    setStreamingMessageId(null);
    setStreamingToolActivity([]);
    // Keep whatever content has arrived
    if (finalContent && msgId) {
      setMessages(prev => prev.map(msg =>
        msg.id === msgId ? {
          ...msg,
          content: finalContent,
          loading: false,
          streaming: false,
        } : msg
      ));
    }
    streamContentRef.current = '';
    setSendingMessage(false);
  }, [streamingMessageId]);

  // eslint-disable-next-line max-lines-per-function, react-hooks/exhaustive-deps
  const sendMessage = async () => {
    if (!inputMessage.trim() || sendingMessage) return;

    // Check if we're in report generation mode and need to continue
    if (reportId && reportStatus === 'asking_questions') {
      try {
        console.log('[SEND-MESSAGE] Continuing report with user response');
        setSendingMessage(true);

        // Add user message to chat
        const userMsg = {
          role: 'user',
          content: inputMessage,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, userMsg]);

        // Clear input
        setInputMessage('');

        // Continue report generation
        const response = await api.continueReportGeneration(reportId, {
          response: inputMessage,
          action: 'continue'
        });

        console.log('[SEND-MESSAGE] Report continue response:', response);

        if (!response.success) {
          setError(`Failed to continue report: ${response.error}`);
        } else {
          // Resume polling after successful continue
          console.log('[SEND-MESSAGE] Resuming polling after user response');
          startReportPolling(reportId, `placeholder_resume_${Date.now()}`, entityId);
        }

        return;
      } catch (error) {
        console.error('[SEND-MESSAGE] Error continuing report:', error);
        setError(`Error: ${error.message}`);
      } finally {
        setSendingMessage(false);
      }
      return;
    }

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString(),
    };

    // Create a placeholder ID outside the try block
    const placeholderId = `placeholder_${Date.now()}`;

    try {
      setSendingMessage(true);
      setMessages(prev => [...prev, userMessage]);
      setInputMessage('');

      // Check if we're in report generation mode
      if (reportId && reportStatus === 'asking_questions') {
        // Send response to report generation session
        const response = await api.continueReportGeneration(reportId, {
          response: userMessage.content,
          action: 'continue'
        });

        if (response.success) {
          // The polling will handle updating the messages
          return;
        } else {
          throw new Error(response.error || 'Failed to continue report generation');
        }
      } else {
        // Normal chat message
        // Create a placeholder for the AI response
        setMessages(prev => [...prev, {
          id: placeholderId,
          role: 'assistant',
          content: '',
          loading: true,
        }]);

        // Include selected prompt ID and note-taking mode in the request
        const promptId = selectedPrompt?.id;

        // Use SSE streaming for progressive token delivery
        setStreamingMessageId(placeholderId);
        setStreamingContent('');
        streamContentRef.current = '';

        try {
          const { abort } = await api.streamChatMessage(
            entityId,
            activeChat,
            userMessage.content,
            { promptId, noteTakingMode },
            {
              onToken: (token) => {
                streamContentRef.current += token;
                // Batch UI updates using requestAnimationFrame
                if (!streamingUpdateTimer.current) {
                  streamingUpdateTimer.current = requestAnimationFrame(() => {
                    flushStreamContent();
                    streamingUpdateTimer.current = null;
                  });
                }
              },
              onToolStart: (toolName) => {
                setStreamingToolActivity(prev => [...prev, { toolName, status: 'running', startedAt: Date.now() }]);
              },
              onToolEnd: (data) => {
                setStreamingToolActivity(prev => prev.map(t =>
                  t.status === 'running' && (!data?.toolName || t.toolName === data.toolName)
                    ? { ...t, status: 'done', completedAt: Date.now() }
                    : t
                ));
              },
              onError: (errorData) => {
                setStreamingMessageId(null);
                setStreamingToolActivity([]);
                streamContentRef.current = '';
                // Show inline error message
                setMessages(prev => prev.map(msg =>
                  msg.id === placeholderId ? {
                    ...msg,
                    role: 'error',
                    loading: false,
                    errorType: errorData.errorType || 'ai_failure',
                    errorMessage: errorData.userMessage || errorData.message || 'An unexpected error occurred.',
                    toolName: errorData.toolName,
                    timestamp: new Date().toISOString(),
                    originalUserMessage: userMessage.content,
                  } : msg
                ));
                setSendingMessage(false);
              },
              onDone: (data) => {
                // Final update with complete content
                setStreamingMessageId(null);
                setStreamingToolActivity([]);
                setMessages(prev => prev.map(msg =>
                  msg.id === placeholderId ? {
                    ...msg,
                    role: 'assistant',
                    content: data.output || streamContentRef.current,
                    loading: false,
                    streaming: false,
                    toolsUsed: data.toolsUsed || [],
                    promptInfo: data.promptInfo,
                    timestamp: new Date().toISOString(),
                  } : msg
                ));
                streamContentRef.current = '';
                setSendingMessage(false);

                // Update chat list to reflect new message
                loadChats();
              },
            }
          );

          streamAbortRef.current = { abort };
        } catch (streamError) {
          // F14: Stream connection failed — show inline error (no batch fallback)
          console.error('Stream connection failed:', streamError);
          setStreamingMessageId(null);
          setStreamingToolActivity([]);
          streamContentRef.current = '';

          setMessages(prev => prev.map(msg =>
            msg.id === placeholderId ? {
              ...msg,
              role: 'error',
              loading: false,
              errorType: 'network_error',
              errorMessage: streamError?.message || 'Failed to connect to chat service. Please try again.',
              timestamp: new Date().toISOString(),
              originalUserMessage: userMessage.content,
            } : msg
          ));
          setSendingMessage(false);
        }

        // After the 3rd user message, the backend auto-generates a title.
        const userMessageCount = messages.filter(m => m.role === 'user').length + 1;
        if (userMessageCount === 3) {
          setTimeout(async () => {
            await loadChats();
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);

      // Parse error type from backend response
      const errorData = error?.response?.data || error?.data || {};
      const errorType = errorData.errorType ||
        (error?.message?.includes('network') || error?.message?.includes('fetch') ? 'network_error' : 'ai_failure');
      const errorMessage = errorData.error || error?.message || 'An unexpected error occurred.';
      const toolName = errorData.toolName;
      const details = errorData.details;

      // Replace placeholder with error message
      setMessages(prev => prev.map(msg =>
        msg.id === placeholderId ? {
          ...msg,
          role: 'error',
          loading: false,
          errorType,
          errorMessage,
          toolName,
          details,
          timestamp: new Date().toISOString(),
          originalUserMessage: inputMessage,
        } : msg
      ));
    } finally {
      setSendingMessage(false);
    }
  };

  // Voice Control Handlers
  const handleActiveMicToggle = async () => {
    // Check if browser is Chrome
    if (!window.chrome && !navigator.userAgent.includes('Chrome')) {
      setShowChromeWarning(true);
      return;
    }

    if (!activeMicMode) {
      // Starting Active Mic mode
      setActiveMicMode(true);
      setVoiceStatus('initializing');

      const voiceCallbacks = {
        onWakeWord: () => {
          setVoiceStatus('recording');
          setIsRecording(true);
          toast.success('Wake word detected! Start speaking...', {
            autoClose: 2000,
            position: 'top-center'
          });
        },
        onRecordingStart: () => {
          setVoiceStatus('recording');
        },
        onTranscript: (text, isFinal) => {
          if (isFinal && text) {
            setInputMessage(prev => prev ? prev + ' ' + text : text);
            setVoiceTranscriptBuffer('');
          }
        },
        onInterim: (text) => {
          setVoiceTranscriptBuffer(text);
        },
        onSendCommand: (remainingText) => {
          if (remainingText) {
            setInputMessage(prev => prev ? prev + ' ' + remainingText : remainingText);
          }
          setVoiceTranscriptBuffer('');
          setTimeout(() => {
            if (sendMessageRef.current) {
              sendMessageRef.current();
            }
          }, 100);
          toast.info('Sending message...', { autoClose: 2000 });
        },
        onClearCommand: () => {
          setInputMessage('');
          setVoiceTranscriptBuffer('');
          toast.info('Message cleared', { autoClose: 2000 });
        },
        onStopRecording: () => {
          setIsRecording(false);
          setVoiceStatus('listening');
        },
        onError: (error) => {
          console.error('Voice error:', error);
          setVoiceStatus('error');
          toast.error(`Voice error: ${error}`, { autoClose: 5000 });
        },
        onLowBattery: () => {
          setActiveMicMode(false);
          setVoiceStatus('idle');
          toast.warning('Voice disabled: Battery level below 20%', { autoClose: 5000 });
        }
      };

      const service = voiceService || new FaanaVoiceService(userPreferences.voice);
      if (!voiceService) {
        setVoiceService(service);
        const lastMessage = messages[messages.length - 1];
        if (lastMessage?.role === 'assistant' && lastMessage.timestamp) {
          lastSpokenMessageRef.current = lastMessage.timestamp;
        }
      }

      const started = service.start(voiceCallbacks);
      if (started) {
        setVoiceStatus('listening');
        toast.success('Active Mic enabled - Say "Hey Coach" to start', {
          autoClose: 3000,
          position: 'top-center'
        });
      } else {
        setActiveMicMode(false);
        setVoiceStatus('idle');
        toast.error('Failed to enable voice recognition', { autoClose: 5000 });
      }
    } else {
      // Stopping Active Mic mode
      setActiveMicMode(false);
      setIsRecording(false);
      setVoiceStatus('idle');
      setVoiceTranscriptBuffer('');

      if (voiceService) {
        voiceService.stop();
      }

      toast.info('Active Mic disabled', { autoClose: 2000 });
    }
  };

  const handleVoiceResponseToggle = () => {
    const newMode = !voiceResponseMode;
    setVoiceResponseMode(newMode);

    // Initialize voice service if needed for TTS
    if (newMode && !voiceService) {
      const service = new FaanaVoiceService(userPreferences.voice);
      setVoiceService(service);

      // When enabling, mark the current last message as already spoken to avoid speaking old messages
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.role === 'assistant' && lastMessage.timestamp) {
        lastSpokenMessageRef.current = lastMessage.timestamp;
      }
    }

    // If turning off while speaking, stop speech
    if (voiceResponseMode && voiceService) {
      voiceService.stopSpeaking();
      // Clear the spoken message tracker when disabling
      lastSpokenMessageRef.current = null;
    }

    toast.info(newMode ? 'Voice Response enabled - AI will speak responses' : 'Voice Response disabled', {
      autoClose: 2000
    });
  };

  // Track the last spoken message to avoid re-speaking
  const lastSpokenMessageRef = useRef(null);

  // Handle voice response when AI responds
  useEffect(() => {
    if (!voiceResponseMode || !voiceService || !userPreferences?.voice?.tts?.enabled) {
      return;
    }

    // Find the latest assistant message
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'assistant' && lastMessage.content && !lastMessage.loading) {
      // Check if we've already spoken this message
      if (lastSpokenMessageRef.current === lastMessage.timestamp) {
        console.log('[Voice] Already spoke this message, skipping');
        return;
      }

      // Extract text content, replacing tokens with type labels and removing formatting
      const textContent = stripTokens(lastMessage.content)
        .replace(/```[\s\S]*?```/g, '') // Remove code blocks
        .replace(/[*_~`]/g, '') // Remove markdown formatting
        .trim();

      if (textContent) {
        console.log('[Voice] Speaking text:', textContent.substring(0, 100) + '...');

        // Mark this message as spoken
        lastSpokenMessageRef.current = lastMessage.timestamp;

        // Set 'generating' immediately — visible during 1-3s Kokoro synthesis gap
        setVoiceStatus('generating');

        // Set callbacks for speaking events
        voiceService.callbacks.onSpeakingStart = () => {
          console.log('[Voice] Started speaking');
          setVoiceStatus('speaking');
        };
        voiceService.callbacks.onSpeakingEnd = () => {
          console.log('[Voice] Finished speaking');
          setVoiceStatus(activeMicMode ? 'listening' : 'idle');
        };

        // Speak with user's TTS settings
        voiceService.speak(textContent);
      }
    }
  }, [messages, voiceResponseMode, voiceService, userPreferences, activeMicMode]);

  // Update voice service when preferences change
  useEffect(() => {
    if (voiceService && userPreferences?.voice) {
      voiceService.updateSettings(userPreferences.voice);
    }
  }, [userPreferences?.voice, voiceService]);

  // Store sendMessage in ref for use in callbacks
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  const handleDeleteChat = async () => {
    if (!selectedChat) return;

    try {
      await api.deleteChat(entityId, selectedChat);

      // If deleting active chat, clear it
      if (activeChat === selectedChat) {
        setActiveChat(null);
        setMessages([]);
      }

      await loadChats();
      setChatMenuAnchor(null);
      setSelectedChat(null);
    } catch (error) {
      console.error('Failed to delete chat:', error);
      setError('Failed to delete chat');
    }
  };

  const handleRenameChat = async () => {
    if (!renamingChat || !newChatTitle.trim()) return;

    try {
      await api.updateChatTitle(entityId, renamingChat, newChatTitle);
      await loadChats();
      setRenamingChat(null);
      setNewChatTitle('');
    } catch (error) {
      console.error('Failed to rename chat:', error);
      setError('Failed to rename chat');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return format(new Date(timestamp), 'h:mm a');
    } catch {
      return '';
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleButtonClick = () => {
    setOpen(true);
  };

  return (
    <>
      {/* Floating Chat Button - Show when chat is closed and not in fullPage mode */}
      {!open && !fullPage && (
        <Box
          sx={{
            position: 'fixed',
            bottom: isMobile ? 16 : 24,
            right: isMobile ? 16 : 24,
            zIndex: 1200,
            overflow: 'visible',
          }}
        >

          {/* Logo button with tooltip */}
          <Tooltip
            title="Faana AI Agent"
            placement="top"
            arrow
            sx={{
              '& .MuiTooltip-tooltip': {
                background: 'rgba(16, 3, 49, 0.95)',
                fontSize: '14px',
                fontWeight: 500,
                padding: '8px 20px',
                borderRadius: '24px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                letterSpacing: '0.3px',
                fontFamily: 'DM Sans, sans-serif',
              },
              '& .MuiTooltip-arrow': {
                color: 'rgba(16, 3, 49, 0.95)',
              },
            }}
          >
            <Box
              onClick={handleButtonClick}
              sx={{
                cursor: 'pointer',
                width: isMobile ? 56 : 64,
                height: isMobile ? 56 : 64,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.3s ease',
                animation: 'fadeIn 0.5s ease-out',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
                '@keyframes fadeIn': {
                  from: {
                    opacity: 0,
                    transform: 'scale(0.5)'
                  },
                  to: {
                    opacity: 1,
                    transform: 'scale(1)'
                  },
                },
              }}
              role="button"
              aria-label="open AI chat"
              tabIndex={0}
            >
              <FaanaLogo size={isMobile ? 56 : 64} />
            </Box>
          </Tooltip>
        </Box>
      )}
      {/* Chat Interface - render as Box in fullPage mode, SwipeableDrawer otherwise */}
      {(() => {
        const chatContent = (
        <>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          ...(fullPage && { margin: 0, padding: 0 })
        }}>
          {/* Header - Simple gray bar on mobile, full AppBar on desktop */}
          {isMobile ? (
            // Mobile: Simple light gray header with clock icon and "Faana" text
            (<Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 1,
                bgcolor: 'grey.100',
                borderBottom: '1px solid',
                borderColor: 'grey.300',
              }}
            >
              <Box
                onClick={() => {
                  const newState = !chatListCollapsed;
                  setChatListCollapsed(newState);
                  localStorage.setItem('chatListCollapsed', JSON.stringify(newState));
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.7
                  }
                }}
                role="button"
                aria-label="toggle chat history"
                tabIndex={0}
              >
                <HistoryIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Faana
                </Typography>
              </Box>
            </Box>)
          ) : (
            // Desktop: Full purple AppBar header
            (<AppBar
              position="relative"
              color="primary"
              elevation={2}
              sx={{
                flexShrink: 0,
                minHeight: 64,
                zIndex: 1310,
                width: '100%',
                bgcolor: 'primary.main',
                color: 'white'
              }}
            >
              <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
                {/* Toggle button for chat history */}
                <Tooltip title={chatListCollapsed ? "Show chat history" : "Hide chat history"}>
                  <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="toggle chat history"
                    onClick={() => {
                      const newState = !chatListCollapsed;
                      setChatListCollapsed(newState);
                      localStorage.setItem('chatListCollapsed', JSON.stringify(newState));
                    }}
                    sx={{
                      mr: 1,
                      bgcolor: !chatListCollapsed ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: !chatListCollapsed ? 'action.selected' : 'action.hover'
                      }
                    }}
                  >
                    {chatListCollapsed ? <MenuIcon /> : <CloseIcon />}
                  </IconButton>
                </Tooltip>

                {/* Avatar */}
                <Box sx={{ mr: 2 }}>
                  <FaanaAvatar size={40} />
                </Box>

                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                      Faana Growth OS AI
                    </Typography>
                    {selectedPrompt && (
                      <Chip
                        label={selectedPrompt.name}
                        size="small"
                        variant="outlined"
                        sx={{
                          color: 'white',
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          fontSize: '0.7rem',
                          height: '20px'
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
                    {entityName || 'Select an entity'} • Neuroscience-based growth coaching
                  </Typography>
                </Box>

              {/* Desktop Toolbar Actions - Keep in header for desktop */}
              {!isMobile && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  {/* Voice Controls - Active Mic Mode */}
                  {userPreferences?.voice?.enabled && (
                  <Tooltip title={activeMicMode ? "Active Mic is ON" : "Enable Active Mic (Always listening for 'Hey Coach')"}>
                    <IconButton
                      onClick={() => handleActiveMicToggle()}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        color: activeMicMode ? 'white' : 'white',
                        bgcolor: activeMicMode ? 'rgba(255, 0, 0, 0.3)' : 'transparent',
                        animation: activeMicMode ? 'pulse-red 2s infinite' : 'none',
                        padding: isMobile ? '4px' : '8px',
                        '&:hover': {
                          bgcolor: activeMicMode ? 'rgba(255, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.1)'
                        },
                        '@keyframes pulse-red': {
                          '0%': {
                            boxShadow: '0 0 0 0 rgba(255, 0, 0, 0.7)',
                          },
                          '70%': {
                            boxShadow: '0 0 0 10px rgba(255, 0, 0, 0)',
                          },
                          '100%': {
                            boxShadow: '0 0 0 0 rgba(255, 0, 0, 0)',
                          },
                        },
                      }}
                    >
                      {activeMicMode ? <MicIcon sx={{ fontSize: 24 }} /> : <MicOffIcon sx={{ fontSize: 24 }} />}
                    </IconButton>
                  </Tooltip>
                )}

                  {/* Voice Controls - Voice Response Mode */}
                  {userPreferences?.voice?.enabled && (
                  <Tooltip title={voiceResponseMode ? "Voice Response is ON" : "Enable Voice Response (AI speaks responses)"}>
                    <IconButton
                      onClick={() => handleVoiceResponseToggle()}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        color: voiceResponseMode ? 'white' : 'white',
                        bgcolor: voiceResponseMode ? 'rgba(33, 150, 243, 0.3)' : 'transparent',
                        animation: voiceResponseMode ? 'pulse-blue 2s infinite' : 'none',
                        padding: isMobile ? '4px' : '8px',
                        '&:hover': {
                          bgcolor: voiceResponseMode ? 'rgba(33, 150, 243, 0.4)' : 'rgba(255, 255, 255, 0.1)'
                        },
                        '@keyframes pulse-blue': {
                          '0%': {
                            boxShadow: '0 0 0 0 rgba(33, 150, 243, 0.7)',
                          },
                          '70%': {
                            boxShadow: '0 0 0 10px rgba(33, 150, 243, 0)',
                          },
                          '100%': {
                            boxShadow: '0 0 0 0 rgba(33, 150, 243, 0)',
                          },
                        },
                      }}
                    >
                      {voiceResponseMode ? <VolumeUpIcon sx={{ fontSize: 24 }} /> : <VolumeOffIcon sx={{ fontSize: 24 }} />}
                    </IconButton>
                  </Tooltip>
                )}

                  {/* Note-taking Mode Toggle */}
                  <Tooltip title={noteTakingMode ? "Note-taking is active" : "Enable note-taking"}>
                    <IconButton
                      onClick={() => setNoteTakingMode(!noteTakingMode)}
                      sx={{
                        color: noteTakingMode ? '#ffeb3b' : 'white',
                        bgcolor: noteTakingMode ? 'rgba(255, 235, 59, 0.1)' : 'transparent',
                        '&:hover': {
                          bgcolor: noteTakingMode ? 'rgba(255, 235, 59, 0.2)' : 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      {noteTakingMode ? <NoteIcon /> : <NoteAddIcon />}
                    </IconButton>
                  </Tooltip>
                  {/* Prompt Selector */}
                  {availablePrompts.length > 0 && (
                  <Tooltip title="Select AI Prompt">
                    <IconButton
                      onClick={() => setShowPromptSelector(true)}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        color: 'white',
                        padding: isMobile ? '4px' : '8px',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      <AgentIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
                  {/* Report Generation */}
                  <Tooltip title="Start Report">
                    <IconButton
                      onClick={() => setShowReportGeneration(true)}
                      sx={{
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)'
                        }
                      }}
                    >
                      <ReportIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                  {/* Close button - only show when not in fullPage mode */}
                  {!fullPage && (
                    <Tooltip title="Hide chat">
                      <IconButton
                        onClick={() => setOpen(false)}
                        sx={{
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)'
                          }
                        }}
                        aria-label="hide chat"
                      >
                        <CloseIcon sx={{ fontSize: 24 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
              </Toolbar>
            </AppBar>)
          )}

          {/* Main Content Area */}
          <Box sx={{
            display: 'flex',
            flexGrow: 1,
            overflow: 'hidden',
            position: 'relative',
            ...(fullPage && { margin: 0, padding: 0 })
          }}>
            {/* Chat List Sidebar - Collapsible */}
            <Paper
              elevation={0}
              sx={{
                width: chatListCollapsed ? 0 : (isMobile ? '280px' : '240px'),
                minWidth: chatListCollapsed ? 0 : (isMobile ? '280px' : '240px'),
                borderRight: chatListCollapsed ? 0 : 1,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                position: 'relative',
                height: '100%',
                bgcolor: 'background.paper',
                boxShadow: chatListCollapsed ? 0 : (isMobile ? 3 : 1),
              }}
            >
              <Box sx={{ p: 1, display: chatListCollapsed ? 'none' : 'block' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Chat History
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  size={isMobile ? "small" : "medium"}
                  onClick={createNewChat}
                  disabled={!entityId}
                >
                  New Chat
                </Button>
              </Box>
              <Divider sx={{ display: chatListCollapsed ? 'none' : 'block' }} />
              <List sx={{
                flexGrow: 1,
                overflow: 'auto',
                display: chatListCollapsed ? 'none' : 'block',
                maxHeight: 'calc(100vh - 200px)',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'background.paper',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'divider',
                  borderRadius: '4px',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                },
              }}>
                {loading && !chats.length ? (
                  Array.from(new Array(3)).map((_, index) => (
                    <ListItem key={index}>
                      <Skeleton variant="text" width="100%" />
                    </ListItem>
                  ))
                ) : (
                  chats.map((chat) => (
                    <ListItem
                      key={chat.chatId}
                      component="div"
                      selected={activeChat === chat.chatId}
                      onClick={() => {
                        console.log('Chat clicked:', chat.chatId);
                        console.log('Current activeChat:', activeChat);
                        if (chat.chatId !== activeChat) {
                          console.log('Setting activeChat to:', chat.chatId);
                          setActiveChat(chat.chatId);
                        } else {
                          console.log('Chat already active, forcing reload');
                          // Force reload the chat history
                          const loadHistory = async () => {
                            try {
                              setLoading(true);
                              setMessages([]);
                              console.log('Force reloading chat history:', { entityId, activeChat: chat.chatId });
                              const response = await api.getChatHistory(entityId, chat.chatId);
                              console.log('Forced reload response:', response);
                              setMessages(response.messages || []);
                            } catch (error) {
                              console.error('Failed to reload chat history:', error);
                              setError('Failed to reload messages');
                              setMessages([]);
                            } finally {
                              setLoading(false);
                            }
                          };
                          loadHistory();
                        }
                      }}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setChatMenuAnchor(e.currentTarget);
                            setSelectedChat(chat.chatId);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={chat.title}
                        secondary={chat.lastMessage ? stripTokens(chat.lastMessage).substring(0, 30) + '...' : 'No messages'}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </Paper>

            {/* Chat Messages Area */}
            <Box sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              ...(fullPage && { margin: 0, padding: 0 })
            }}>
              {!entityId ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Alert severity="info">
                    Please select an entity to start chatting
                  </Alert>
                </Box>
              ) : !activeChat ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Select a chat or create a new one to begin
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Messages */}
                  <Box sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    p: 2,
                    ...(fullPage && { height: '100%' })
                  }}>
                    {/* Debug info */}
                    {messages.length === 0 && !loading && (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          No messages in this chat yet. Send a message to start the conversation.
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Chat ID: {activeChat}
                        </Typography>
                      </Box>
                    )}
                    {loading && !messages.length ? (
                      Array.from(new Array(3)).map((_, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Skeleton variant="rectangular" height={60} />
                        </Box>
                      ))
                    ) : (
                      // eslint-disable-next-line max-lines-per-function
                      (messages.map((message, index) => (
                        message.role === 'error' ? (
                          <Box key={index} sx={{ mb: 2 }}>
                            <ChatErrorMessage
                              errorType={message.errorType}
                              message={message.errorMessage}
                              toolName={message.toolName}
                              details={message.details}
                              timestamp={message.timestamp}
                              onRetry={message.originalUserMessage ? () => {
                                // Remove error message and resend
                                setMessages(prev => prev.filter(m => m.id !== message.id));
                                setInputMessage(message.originalUserMessage);
                                // Trigger send after state update
                                setTimeout(() => {
                                  const sendBtn = document.querySelector('[data-testid="send-button"]');
                                  if (sendBtn) sendBtn.click();
                                }, 100);
                              } : undefined}
                            />
                          </Box>
                        ) : (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                            mb: 2,
                          }}
                        >
                          {message.role === 'assistant' && (
                            <Box sx={{ mr: 1 }}>
                              <FaanaAvatar size={40} />
                            </Box>
                          )}
                          <Paper
                            elevation={1}
                            sx={{
                              p: 2,
                              maxWidth: '70%',
                              bgcolor: message.role === 'user' ? 'primary.main' : 'grey.100',
                              color: message.role === 'user' ? 'white' : 'text.primary',
                            }}
                          >
                            {message.id === streamingMessageId ? (
                              <StreamingMessage
                                content={streamingContent}
                                isStreaming={true}
                                toolActivity={streamingToolActivity}
                              />
                            ) : message.loading && !message.isReportGeneration ? (
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <CircularProgress size={16} color={message.role === 'user' ? 'inherit' : 'primary'} />
                                  <Typography variant="body2">
                                    {message.content || 'Thinking...'}
                                  </Typography>
                                </Box>
                                {message.reportStatus && (
                                  <Box sx={{ ml: 3, mb: 1 }}>
                                    <Typography variant="caption" sx={{ display: 'block', opacity: 0.7 }}>
                                      Status: {message.reportStatus}
                                      {message.pollCount && ` • Poll ${message.pollCount}`}
                                    </Typography>
                                    {message.progressCount > 0 && (
                                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.7 }}>
                                        Progress: {message.progressCount} tool operations completed
                                      </Typography>
                                    )}
                                    {message.progressSummary && (
                                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, fontStyle: 'italic' }}>
                                        {message.progressSummary}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                                {/* Real-time MCP Logs for loading messages */}
                                {message.mcpLogs && message.mcpLogs.length > 0 && (
                                  <Box
                                    sx={{
                                      mt: 1,
                                      p: 1,
                                      bgcolor: 'action.hover',
                                      borderRadius: 1,
                                      maxHeight: 120,
                                      overflow: 'auto'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                        🔧 Live Progress Updates:
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleMcpLogsCollapse(index)}
                                        sx={{ color: 'text.secondary', p: 0.25 }}
                                      >
                                        {message.mcpLogsCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                                      </IconButton>
                                    </Box>
                                    <Collapse in={!message.mcpLogsCollapsed}>
                                      {message.mcpLogs.slice(-8).map((log, logIndex) => (
                                        <Typography key={logIndex} variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1.2 }}>
                                          {new Date(log.timestamp).toLocaleTimeString()} - {log.tool}: {log.data?.query || log.data?.entityId || log.data?.operation || 'processing...'}
                                        </Typography>
                                      ))}
                                      {message.mcpLogs.length > 8 && (
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic', fontSize: '0.65rem' }}>
                                          +{message.mcpLogs.length - 8} more operations...
                                        </Typography>
                                      )}
                                    </Collapse>
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <>
                                {/* Collapsible message content */}
                                {message.isCollapsible ? (
                                  <Box>
                                    {message.isCollapsed ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ flex: 1 }}>
                                          <TokenizedText
                                            content={message.contentSummary || message.content.split('\n')[0]}
                                            entityId={entityId}
                                            variant="body2"
                                          />
                                        </Box>
                                        <IconButton
                                          size="small"
                                          onClick={() => toggleMessageCollapse(index)}
                                          sx={{ color: message.role === 'user' ? 'white' : 'inherit' }}
                                        >
                                          <ExpandMoreIcon />
                                        </IconButton>
                                      </Box>
                                    ) : (
                                      <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                          <Box sx={{ flex: 1 }}>
                                            <TokenizedText
                                              content={message.content}
                                              entityId={entityId}
                                              variant="body2"
                                              sx={{ whiteSpace: 'pre-wrap' }}
                                            />
                                          </Box>
                                          <IconButton
                                            size="small"
                                            onClick={() => toggleMessageCollapse(index)}
                                            sx={{ color: message.role === 'user' ? 'white' : 'inherit' }}
                                          >
                                            <ExpandLessIcon />
                                          </IconButton>
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                ) : (
                                  message.isReportGeneration ? (
                                    <VariableProcessingDisplay
                                      reportStatus={message.reportStatus}
                                      variables={message.variables || []}
                                      totalVariables={message.totalVariables || 0}
                                      processedVariables={message.processedVariables || 0}
                                      errorCount={message.errorCount || 0}
                                      insufficientDataCount={message.insufficientDataCount || 0}
                                      loading={message.loading}
                                    />
                                  ) : message.isReportCompletion ? (
                                    <ReportCompletionDisplay
                                      totalVariables={message.totalVariables}
                                      completedVariables={message.completedVariables || []}
                                      errorVariables={message.errorVariables || []}
                                      insufficientDataVariables={message.insufficientDataVariables || []}
                                      reportId={message.reportId}
                                      onPreviewClick={async () => {
                                        try {
                                          // Compile and preview the report
                                          const response = await api.compileReport(message.reportId, entityId);
                                          if (response.success) {
                                            setReportPreview({
                                              open: true,
                                              content: response.compiledReport,
                                              reportId: message.reportId,
                                              savedReportId: response.savedReportId
                                            });
                                          } else {
                                            setError('Failed to compile report: ' + response.error);
                                          }
                                        } catch (error) {
                                          setError('Failed to preview report: ' + error.message);
                                        }
                                      }}
                                    />
                                  ) : (
                                    <TokenizedText
                                      content={message.content}
                                      entityId={entityId}
                                      variant="body2"
                                      sx={{ whiteSpace: 'pre-wrap' }}
                                    />
                                  )
                                )}

                                {/* Report Preview Button - now integrated into ReportCompletionDisplay */}

                                {/* Debug info */}
                                {message.role === 'assistant' && index === messages.length - 1 && (
                                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                                    Debug: reportStatus={reportStatus}, reportId={reportId}, hasQuestions={message.content.includes('questions')}
                                  </Typography>
                                )}

                                {/* Show input prompt when AI is waiting */}
                                {message.role === 'assistant' &&
                                 index === messages.length - 1 &&
                                 reportId &&
                                 reportStatus === 'asking_questions' && (
                                  <Box sx={{ mt: 2, p: 2, bgcolor: 'info.main', color: 'white', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                      ⌨️ Your Response Needed
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                      The AI is waiting for your input. You can either:
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                      <li>Answer the specific questions asked above</li>
                                      <li>Type "Please continue with the report" to skip</li>
                                      <li>Click the button below for a quick continue</li>
                                    </ul>
                                    <Button
                                      sx={{ mt: 2 }}
                                      variant="contained"
                                      size="medium"
                                      color="primary"
                                      fullWidth
                                      onClick={async () => {
                                        try {
                                          console.log('[MANUAL-CONTINUE] User clicked continue button');
                                          console.log('[MANUAL-CONTINUE] Report Session ID:', reportId);
                                          console.log('[MANUAL-CONTINUE] Current report status:', reportStatus);
                                          setSendingMessage(true);
                                          const response = await api.continueReportGeneration(reportId, {
                                            response: 'Please proceed with generating a comprehensive report using all available data. Focus on key insights and actionable recommendations.',
                                            action: 'continue'
                                          });
                                          console.log('[MANUAL-CONTINUE] Response:', response);
                                          if (!response.success) {
                                            setError(`Failed to continue: ${response.error}`);
                                            // Add error to chat
                                            setMessages(prev => [...prev, {
                                              role: 'system',
                                              content: `❌ Error: ${response.error || 'Failed to continue report generation'}`,
                                              timestamp: new Date().toISOString()
                                            }]);
                                          } else {
                                            // Resume polling after successful continue
                                            console.log('[MANUAL-CONTINUE] Resuming polling');
                                            startReportPolling(reportId, `placeholder_manual_${Date.now()}`, entityId);
                                          }
                                        } catch (error) {
                                          console.error('[MANUAL-CONTINUE] Error:', error);
                                          setError(`Error continuing report: ${error.message}`);
                                          // Add error to chat
                                          setMessages(prev => [...prev, {
                                            role: 'system',
                                            content: `❌ Error: ${error.message}`,
                                            timestamp: new Date().toISOString()
                                          }]);
                                        } finally {
                                          setSendingMessage(false);
                                        }
                                      }}
                                      disabled={sendingMessage}
                                    >
                                      {sendingMessage ? 'Continuing...' : 'Skip Questions - Generate Report Now'}
                                    </Button>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                                      Or type your answers in the message box below
                                    </Typography>
                                  </Box>
                                )}

                                {/* MCP Logs for Report Generation */}
                                {message.isReportGeneration && message.mcpLogs && message.mcpLogs.length > 0 && (
                                  <Box
                                    sx={{
                                      mb: 2,
                                      p: 1.5,
                                      bgcolor: 'warning.light',
                                      borderRadius: 1,
                                      border: 1,
                                      borderColor: 'warning.main',
                                      opacity: 0.9
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                                        🔧 Report Generation Progress:
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleMcpLogsCollapse(index)}
                                        sx={{ color: 'warning.dark', p: 0.5 }}
                                      >
                                        {message.mcpLogsCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                                      </IconButton>
                                    </Box>
                                    <Collapse in={!message.mcpLogsCollapsed}>
                                      {message.mcpLogs.slice(-5).map((log, logIndex) => (
                                        <Typography key={logIndex} variant="caption" sx={{ display: 'block', color: 'warning.dark', ml: 1 }}>
                                          • {new Date(log.timestamp).toLocaleTimeString()}: {log.tool} {log.type === 'mcp_response' ? '✓' : '→'} {log.data?.query || log.data?.entityId || ''}
                                        </Typography>
                                      ))}
                                      {message.mcpLogs.length > 5 && (
                                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', ml: 1, fontStyle: 'italic' }}>
                                          ...and {message.mcpLogs.length - 5} more operations
                                        </Typography>
                                      )}
                                    </Collapse>
                                  </Box>
                                )}

                                {/* MCP Tool Usage Summary - Displayed Above Response */}
                                {!message.isReportGeneration && message.toolsUsed && message.toolsUsed.length > 0 && (
                                  <Box
                                    sx={{
                                      mb: 2,
                                      p: 1.5,
                                      bgcolor: 'info.light',
                                      borderRadius: 1,
                                      border: 1,
                                      borderColor: 'info.main',
                                      opacity: 0.9
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'info.dark', display: 'block', mb: 1 }}>
                                      🔍 AI Actions Performed:
                                    </Typography>
                                    {message.toolsUsed.map((tool, toolIndex) => (
                                      <Box key={toolIndex} sx={{ mb: 0.5 }}>
                                        {tool.tool === 'list_entity_resources' && (
                                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                            <Typography variant="caption" sx={{ color: 'info.dark', fontWeight: 500 }}>
                                              📋 Listed available resources
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                              ({tool.resourcesAccessed.length} items found)
                                            </Typography>
                                          </Box>
                                        )}
                                        {tool.tool === 'search_tokenized_documents' && (
                                          <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                              <Typography variant="caption" sx={{ color: 'info.dark', fontWeight: 500 }}>
                                                🔎 Searched documents
                                              </Typography>
                                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                for "{typeof tool.input === 'string' ? JSON.parse(tool.input).query : tool.input.query}"
                                              </Typography>
                                            </Box>
                                            {tool.resourcesAccessed.length > 0 && (
                                              <Box sx={{ ml: 3, mt: 0.5 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                  Found {tool.resourcesAccessed.length} matching document{tool.resourcesAccessed.length > 1 ? 's' : ''}:
                                                </Typography>
                                                {tool.resourcesAccessed.slice(0, 3).map((resource, idx) => (
                                                  <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'info.dark', ml: 1 }}>
                                                    • {resource.name}
                                                  </Typography>
                                                ))}
                                                {tool.resourcesAccessed.length > 3 && (
                                                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', ml: 1 }}>
                                                    • ...and {tool.resourcesAccessed.length - 3} more
                                                  </Typography>
                                                )}
                                              </Box>
                                            )}
                                          </Box>
                                        )}
                                        {tool.tool === 'read_entity_resource' && tool.resourcesAccessed.length > 0 && (
                                          <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                              <Typography variant="caption" sx={{ color: 'info.dark', fontWeight: 500 }}>
                                                📄 Read document
                                              </Typography>
                                            </Box>
                                            <Box sx={{ ml: 3, mt: 0.5 }}>
                                              {tool.resourcesAccessed.map((resource, idx) => (
                                                <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'info.dark' }}>
                                                  • {resource.uri.split('/').pop() || resource.uri}
                                                </Typography>
                                              ))}
                                            </Box>
                                          </Box>
                                        )}
                                      </Box>
                                    ))}

                                    {/* Expandable Technical Details */}
                                    {message.toolsUsed.length > 0 && (
                                      <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                        <MCPToolDetails tools={message.toolsUsed} />
                                      </Box>
                                    )}
                                  </Box>
                                )}

                                {/* Prompt Display for Assistant Messages */}
                                {message.role === 'assistant' && message.fullPrompt && (
                                  <PromptDisplay fullPrompt={message.fullPrompt} />
                                )}

                                {message.timestamp && (
                                  <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.7 }}>
                                    {formatTimestamp(message.timestamp)}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Paper>
                          {message.role === 'user' && (
                            <Box sx={{ ml: 1 }}>
                              <UserAvatar user={user} size={40} />
                            </Box>
                          )}
                        </Box>
                        )
                      )))
                    )}
                    <div ref={messagesEndRef} />
                  </Box>

                  {/* Mobile Bottom Toolbar */}
                  {isMobile && (
                    <>
                      <Divider />
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-around',
                        p: 1,
                        bgcolor: 'background.paper',
                        borderTop: 1,
                        borderColor: 'divider'
                      }}>
                        {/* Note-taking Mode */}
                        <Tooltip title={noteTakingMode ? "Note-taking is active" : "Enable note-taking"}>
                          <IconButton
                            onClick={() => setNoteTakingMode(!noteTakingMode)}
                            size="small"
                            sx={{
                              color: noteTakingMode ? '#ffeb3b' : 'text.secondary',
                              bgcolor: noteTakingMode ? 'rgba(255, 235, 59, 0.1)' : 'transparent',
                            }}
                          >
                            {noteTakingMode ? <NoteIcon fontSize="small" /> : <NoteAddIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>

                        {/* Report Generation */}
                        <Tooltip title="Start Report">
                          <IconButton
                            onClick={() => setShowReportGeneration(true)}
                            size="small"
                            sx={{ color: 'text.secondary' }}
                          >
                            <ReportIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Prompt Selector */}
                        {availablePrompts.length > 0 && (
                          <Tooltip title="Select AI Prompt">
                            <IconButton
                              onClick={() => setShowPromptSelector(true)}
                              size="small"
                              sx={{ color: 'text.secondary' }}
                            >
                              <AgentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Voice Controls */}
                        {userPreferences?.voice?.enabled && (
                          <>
                            <Tooltip title={activeMicMode ? "Active Mic ON" : "Enable Active Mic"}>
                              <IconButton
                                onClick={() => handleActiveMicToggle()}
                                size="small"
                                sx={{
                                  color: activeMicMode ? 'error.main' : 'text.secondary',
                                  bgcolor: activeMicMode ? 'rgba(255, 0, 0, 0.1)' : 'transparent',
                                }}
                              >
                                {activeMicMode ? <MicIcon fontSize="small" /> : <MicOffIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>

                            <Tooltip title={voiceResponseMode ? "Voice Response ON" : "Enable Voice Response"}>
                              <IconButton
                                onClick={() => handleVoiceResponseToggle()}
                                size="small"
                                sx={{
                                  color: voiceResponseMode ? 'primary.main' : 'text.secondary',
                                  bgcolor: voiceResponseMode ? 'rgba(33, 150, 243, 0.1)' : 'transparent',
                                }}
                              >
                                {voiceResponseMode ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </>
                        )}

                        {/* Settings */}
                      </Box>
                    </>
                  )}

                  {/* Input Area */}
                  <Divider />
                  <Box sx={{ p: 2, pb: isMobile ? 9 : 2, bgcolor: 'background.paper' }}>
                    {error && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                      </Alert>
                    )}
                    {noteTakingMode && (
                      <Alert
                        severity="info"
                        icon={<NoteIcon />}
                        sx={{ mb: 2 }}
                      >
                        Note-taking is active. Your conversation is being saved as a permanent note for {entityName}.
                      </Alert>
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        placeholder={
                          reportId && reportStatus === 'asking_questions'
                            ? "Answer the AI's questions or type 'Please continue with the report' to skip..."
                            : "Ask about leadership growth, team dynamics, or workplace challenges..."
                        }
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={sendingMessage}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                          },
                        }}
                      />
                      {streamingMessageId ? (
                        <IconButton
                          onClick={handleStopStreaming}
                          sx={{ color: 'error.main' }}
                          title="Stop generating"
                        >
                          <StopIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          color="primary"
                          onClick={sendMessage}
                          disabled={!inputMessage.trim() || sendingMessage}
                          data-testid="send-button"
                        >
                          <SendIcon />
                        </IconButton>
                      )}
                    </Box>

                    {/* Voice Status Indicator */}
                    {(activeMicMode || voiceStatus === 'generating' || voiceStatus === 'speaking') && (
                      <Box
                        aria-live="polite"
                        sx={{
                          mt: 1,
                          p: 1,
                          borderRadius: 2,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: voiceStatus === 'recording' ? 'error.main' :
                                      voiceStatus === 'listening' ? 'warning.main' :
                                      voiceStatus === 'speaking' || voiceStatus === 'generating' ? 'info.main' : 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        {voiceStatus === 'generating' && (
                          <>
                            <CircularProgress size={16} sx={{ color: 'info.main' }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Generating speech...
                            </Typography>
                          </>
                        )}
                        {voiceStatus === 'listening' && (
                          <>
                            <HearingIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Listening for "Hey Coach"...
                            </Typography>
                          </>
                        )}
                        {voiceStatus === 'recording' && (
                          <>
                            <MicIcon sx={{ color: 'error.main', fontSize: 20, animation: 'pulse 1s infinite' }} />
                            <Typography variant="caption" sx={{ color: 'text.primary', flex: 1 }}>
                              Recording: {voiceTranscriptBuffer || 'Speak now...'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Say "send now" or "clear message"
                            </Typography>
                          </>
                        )}
                        {voiceStatus === 'speaking' && (
                          <>
                            <VolumeUpIcon sx={{ color: 'info.main', fontSize: 20 }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              AI is speaking...
                            </Typography>
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Chat Menu */}
        <Menu
          anchorEl={chatMenuAnchor}
          open={Boolean(chatMenuAnchor)}
          onClose={() => {
            setChatMenuAnchor(null);
            setSelectedChat(null);
          }}
        >
          <MenuItem
            onClick={() => {
              const chat = chats.find(c => c.chatId === selectedChat);
              setRenamingChat(selectedChat);
              setNewChatTitle(chat?.title || '');
              setChatMenuAnchor(null);
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Rename
          </MenuItem>
          <MenuItem onClick={handleDeleteChat}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Rename Dialog */}
        <Dialog
          open={Boolean(renamingChat)}
          onClose={() => {
            setRenamingChat(null);
            setNewChatTitle('');
          }}
        >
          <DialogTitle>Rename Chat</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              value={newChatTitle}
              onChange={(e) => setNewChatTitle(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleRenameChat();
                }
              }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setRenamingChat(null);
              setNewChatTitle('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleRenameChat} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Prompt Selector Dialog */}
        <Dialog
          open={showPromptSelector}
          onClose={() => setShowPromptSelector(false)}
          maxWidth="md"
          fullWidth
          sx={{
            zIndex: 1400, // Higher than Drawer (1300) to appear on top
          }}
        >
          <DialogTitle>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              Select an Agent
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.primary', opacity: 0.8 }}>
              Choose how Faana AI should behave and respond
            </Typography>
          </DialogTitle>
          <DialogContent>
            {loadingPrompts ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List>
                {availablePrompts.map((prompt) => (
                  <ListItem
                    key={prompt.id}
                    button
                    selected={selectedPrompt?.id === prompt.id}
                    onClick={() => {
                      setSelectedPrompt(prompt);
                      setShowPromptSelector(false);
                    }}
                    sx={{
                      border: 1,
                      borderColor: selectedPrompt?.id === prompt.id ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        borderColor: 'primary.main'
                      }
                    }}
                  >
                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 500 }}>
                            {prompt.name}
                          </Typography>
                          {prompt.isDefault && (
                            <Chip label="Default" size="small" color="primary" />
                          )}
                          <Chip
                            label={`v${prompt.version}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1, color: 'text.primary', opacity: 0.9 }}>
                            {prompt.description}
                          </Typography>
                          {prompt.metadata?.model && (
                            <Typography variant="caption" sx={{ color: 'text.primary', opacity: 0.7 }}>
                              Model: {prompt.metadata.model}
                              {prompt.metadata.temperature !== undefined &&
                                ` • Temperature: ${prompt.metadata.temperature}`}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPromptSelector(false)}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Report Generation Dialog */}
        <ReportGeneration
          open={showReportGeneration}
          onClose={() => setShowReportGeneration(false)}
          entityId={entityId}
          onStartReport={handleStartReport}
        />

        {/* Report Preview Modal */}
        <Dialog
          open={reportPreview.open}
          onClose={() => setReportPreview({ open: false, content: '', reportId: null, savedReportId: null })}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: { height: '90vh' }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Report Preview</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Device Toggle */}
                <ToggleButtonGroup
                  value={previewDevice}
                  exclusive
                  onChange={(event, newDevice) => {
                    if (newDevice !== null) {
                      setPreviewDevice(newDevice);
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="desktop" aria-label="desktop">
                    <ComputerIcon />
                  </ToggleButton>
                  <ToggleButton value="tablet" aria-label="tablet">
                    <TabletIcon />
                  </ToggleButton>
                  <ToggleButton value="mobile" aria-label="mobile">
                    <PhoneIcon />
                  </ToggleButton>
                </ToggleButtonGroup>

                <IconButton
                  onClick={() => setReportPreview({ open: false, content: '', reportId: null, savedReportId: null })}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
            <Box
              sx={{
                ...getDeviceDimensions(previewDevice),
                maxHeight: '70vh',
                overflow: 'auto',
                bgcolor: 'white',
                boxShadow: previewDevice !== 'desktop' ? 3 : 0,
                borderRadius: previewDevice !== 'desktop' ? 2 : 0,
                margin: previewDevice !== 'desktop' ? 2 : 0,
              }}
            >
              {/* HTML Renderer */}
              <Box
                dangerouslySetInnerHTML={{ __html: reportPreview.content }}
                sx={{
                  width: '100%',
                  '& *': {
                    maxWidth: '100%',
                  },
                  // Add responsive styles for different devices
                  fontSize: previewDevice === 'mobile' ? '0.875rem' :
                          previewDevice === 'tablet' ? '0.95rem' : '1rem',
                  lineHeight: 1.5,
                  p: previewDevice === 'desktop' ? 3 : 2,
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
              {previewDevice === 'mobile' && 'Mobile View (375px)'}
              {previewDevice === 'tablet' && 'Tablet View (768px)'}
              {previewDevice === 'desktop' && 'Desktop View (Full Width)'}
            </Typography>
            <Button onClick={() => setReportPreview({ open: false, content: '', reportId: null, savedReportId: null })}>
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                // Navigate to the saved report in Documents
                window.location.href = `/entity/${entityId}/documents?tab=reports&id=${reportPreview.savedReportId}`;
              }}
            >
              View in Documents
            </Button>
          </DialogActions>
        </Dialog>
        </>
        );

        // Wrap content based on mode
        if (fullPage) {
          return (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              width: '100%',
              margin: 0,
              padding: 0,
              overflow: 'hidden'
            }}>
              {chatContent}
            </Box>
          );
        } else {
          return (
            <SwipeableDrawer
              anchor="right"
              open={open}
              onClose={() => setOpen(false)}
              onOpen={() => setOpen(true)}
              disableSwipeToOpen={false}
              sx={{
                zIndex: 1300, // Higher than Material-UI AppBar default (1100)
              }}
              PaperProps={{
                sx: {
                  width: {
                    xs: '100%', // Full width on mobile
                    sm: '85%',  // 85% on small tablets
                    md: 600,    // Fixed 600px on desktop
                    lg: 700     // Fixed 700px on large screens
                  },
                  height: '100%',
                  zIndex: 1300, // Ensure paper is also above other elements
                }
              }}
            >
              {chatContent}
            </SwipeableDrawer>
          );
        }
      })()}
      {/* Chrome Warning Dialog */}
      <Dialog
        open={showChromeWarning}
        onClose={() => setShowChromeWarning(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ color: 'warning.main' }} />
          Browser Not Supported
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Voice features require Google Chrome browser for the best experience.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The Web Speech API used for voice recognition and synthesis is currently
            only fully supported in Chrome. Please switch to Chrome to use voice features.
          </Typography>
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              To enable voice features:
            </Typography>
            <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Open this page in Google Chrome</li>
              <li>Go to Settings → Voice</li>
              <li>Enable voice features</li>
              <li>Allow microphone permissions when prompted</li>
            </ol>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChromeWarning(false)} color="primary">
            Understood
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChatBot;